import { ApplicationRef, Component, OnDestroy, OnInit } from '@angular/core'
import { AuthService } from '@services/auth.service'
import { ContextService } from '@services/context.service'
import { LoggingService } from '@services/logging.service'
import { OrganisationService } from '@services/organisation.service'
import { RequestedBookingsCountPublisher } from '@app/domain/RequestedBookingsCountPublisher'
import { WaitlistEntriesCountPublisher } from '@app/domain/WaitlistEntriesCountPublisher'
import { MessagesUnreadCountPublisher } from '@app/domain/MessagesUnreadCountPublisher'
import { Router } from '@angular/router'
import { Subject, forkJoin, takeUntil, first, mergeMap, of } from 'rxjs'
import { SseService } from '@services/sse.service'
import { ToolbarService } from '@services/toolbar.service'
import { ThemeService } from '@services/theme.service'
import { UpdateService } from '@services/update.service'
import { UserService } from '@services/user.service'

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'TableSense'
    private onDestroy$ = new Subject<void>()

    constructor(
        private appRef: ApplicationRef,
        private authService: AuthService,
        private contextService: ContextService,
        private loggingService: LoggingService,
        private organisationService: OrganisationService,
        private messagesUnreadCountPublisher: MessagesUnreadCountPublisher,
        private requestedBookingsCountPublisher: RequestedBookingsCountPublisher,
        private waitlistEntriesCountPublisher: WaitlistEntriesCountPublisher,
        private sseService: SseService,
        public toolbarService: ToolbarService,
        public themeService: ThemeService,
        private updateService: UpdateService,
        private userService: UserService,
        public router: Router
    ) {
        const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable))
        appIsStable$.subscribe(() => this.loggingService.setUp())
    }

    ngOnInit() {
        this.updateService.startCheckingForUpdates()
        this.authService.loggedIn$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(loggedIn => {
                if (loggedIn) {
                    this.initialiseStack()
                } else {
                    this.waitlistEntriesCountPublisher.setTrackedDate(null)
                    this.sseService.disconnect()
                }
            })
        this.contextService.venue$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(venue => {
                const business = this.contextService.getBusiness()
                if (business) {
                    this.sseService.connect(business.id, venue.id)
                }
            })
        this.authService.checkLoggedInStatus()
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    private initialiseStack() {
        this.organisationService.getCached()
            .pipe(
                mergeMap(organisation => forkJoin([
                    this.userService.getCached(),
                    of(organisation),
                ])),
                takeUntil(this.onDestroy$)
            )
            .subscribe(([user, organisation]) => {
                this.contextService.setUpObjectGraph(organisation, user)
                this.requestedBookingsCountPublisher.startPollingForRequestedBookings()
                this.waitlistEntriesCountPublisher.startPolling()
                this.messagesUnreadCountPublisher.startPolling()
                this.requestedBookingsCountPublisher.refreshNow()
                this.waitlistEntriesCountPublisher.refreshNow()
                this.messagesUnreadCountPublisher.refreshNow()
            })
    }
}
